/**
 * Lab Detail (`member-management`) tabs in Strix UI.
 *
 * Every tab shows the full Lab sheet for the **selected** downline member —
 * complete layouts with 0 / empty rows when a figure is not on Agent-API yet.
 * No "unavailable" holes that hide Lab furniture.
 */
import { useMemo, useRef, useState } from 'react'
import {
  DEFAULT_PAGE_SIZE,
  Banner,
  Button,
  Card,
  Chip,
  DataTable,
  Field,
  FilterBar,
  Input,
  Kpi,
  Legend,
  Modal,
  Pager,
  Popover,
  Segmented,
  Select,
  Skeleton,
  Spinner,
  StateBlock,
  Textarea,
  useToast,
  type ActiveFilter,
  type Column,
} from '@w7/ui'
import {
  useDeleteMemberBlockedDeviceId,
  useEventsForPll,
  useFancyBookmakerCommission,
  useGames,
  useLoginHistory,
  useMemberActivityMatrix,
  useMemberBets,
  useSaveMemberBlockedDeviceId,
  useSubGames,
  useTransfers,
} from '@/app/queries'
import { ApiError } from '@/api/client'
import { BetListMemberNamePicker } from '@/features/member/BetListMemberNamePicker'
import { Amount, Money } from '@/app/bits'
import {
  ACTIVITY_METRICS,
  ACTIVITY_PERIODS,
  balanceLinesFromAccount,
  isoDayLocal,
  isActivityRemovedFilterName,
  LAB_TRANSFER_MAX_DAYS,
  labMemberCommissionDisplay,
  labTransferNetShare,
  labTransferPayerPayee,
  labTransferRangeDays,
  labTransferSignedAmount,
  type ActivityPeriod,
  type LoginHistoryRow,
} from '@/api/map/memberDetailLab'
import {
  memberBetProfitLiability,
  type MemberBetTime,
  type MemberBetType,
  type MemberSportBookBet,
} from '@/api/map/memberBets'
import { betListMatchedUnmatchedAmt } from '@/features/net-exposure/betListMatchedAmt'
import { limitUsage, round2 } from '@/lib/book'
import { parseCurrencyType } from '@/lib/currency'
import {
  EMPTY,
  displayMoney,
  displayMoneyCompact,
  lll,
  money,
} from '@/lib/format'
import { formatLineSessionDisplayRun } from '@/lib/lineSessionDisplayRun'
import type { Account, DateRange, Transfer } from '@/data/types'

type Range = DateRange & { label: string; days: number }
type TransferRow = Transfer & { from: Account | null; to: Account | null }

/** Bet List Odds Placed — line ladder uses Back/Lay Run from `runs`. */
export function memberBetListOddsPlaced(
  b: Pick<MemberSportBookBet, 'isLineLadder' | 'runs' | 'side' | 'oddsPlaced'>,
): string {
  if (b.isLineLadder) {
    return b.runs != null ? formatLineSessionDisplayRun(b.runs, b.side) || EMPTY : EMPTY
  }
  return b.oddsPlaced ? b.oddsPlaced.toFixed(2) : EMPTY
}

/* ============================================================== activity == */

/** Lab Activity — period matrix + Agent / All Members + game + custom date filters. */
export function LabActivityTab({ account }: { account: Account; range: Range }) {
  const today = isoDayLocal()
  const [memberScope, setMemberScope] = useState<'agent' | 'all-members'>('agent')
  const [gameId, setGameId] = useState('all')
  const [subGame, setSubGame] = useState('All')
  const [typeName, setTypeName] = useState('All')
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({
    memberScope: 'agent' as 'agent' | 'all-members',
    gameId: 'all',
    subGame: 'All',
    typeName: 'All',
    fromDate: today,
    toDate: today,
  })
  const [searched, setSearched] = useState(true)

  const games = useGames()
  const subGames = useSubGames(gameId === 'all' ? null : gameId)
  const matrixQ = useMemberActivityMatrix(
    {
      memberId: account.id,
      memberScope: applied.memberScope,
      fromDate: applied.fromDate,
      toDate: applied.toDate,
      // ponytail: game/sub/type not on analytics detail yet — kept for Lab UI parity + query key.
      gameId: applied.gameId === 'all' ? undefined : applied.gameId,
      subGame: applied.subGame === 'All' ? undefined : applied.subGame,
      typeName: applied.typeName === 'All' ? undefined : applied.typeName,
    },
    searched,
  )

  const matrix = matrixQ.data
  const ccy = parseCurrencyType(account.currencyType)
  const customLabel = `${applied.fromDate} – ${applied.toDate}`

  type MatrixRow = { id: string; label: string; values: Record<ActivityPeriod, number> }
  const rows: MatrixRow[] = useMemo(() => {
    if (!matrix) return []
    return ACTIVITY_METRICS.map((m) => ({
      id: m.id,
      label: m.label,
      values: matrix.values[m.id],
    }))
  }, [matrix])

  const columns: Column<MatrixRow>[] = useMemo(
    () => [
      {
        id: 'metric',
        header: '',
        cell: (row) => <b>{row.label}</b>,
        sortValue: (row) => row.label,
        width: 140,
      },
      ...ACTIVITY_PERIODS.map(
        (p): Column<MatrixRow> => ({
          id: p.id,
          header: p.label,
          numeric: true,
          cell: (row) => (
            <Amount value={row.values[p.id]} currency={account.currencyType} alreadyDisplay />
          ),
          sortValue: (row) => row.values[p.id],
        }),
      ),
    ],
    [account.currencyType],
  )

  const gameOptions = [
    { value: 'all', label: 'All' },
    ...(games.data ?? [])
      .filter((g) => !isActivityRemovedFilterName(g.name))
      .map((g) => ({ value: g._id, label: g.name })),
  ]
  const subOptions = [
    { value: 'All', label: 'All' },
    ...(subGames.data ?? [])
      .filter((name) => !isActivityRemovedFilterName(name))
      .map((name) => ({ value: name, label: name })),
  ]

  const markDirty = () => setSearched(false)

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Activity filters">
        {account.role !== 'player' ? (
          <div className="w7-filterbar-row">
            <Segmented
              label="Member scope"
              value={memberScope}
              onChange={(v) => {
                setMemberScope(v)
                markDirty()
              }}
              items={[
                { id: 'agent', label: 'Agent' },
                { id: 'all-members', label: 'All Members' },
              ]}
            />
          </div>
        ) : null}
        <div className="w7-filterbar-row" style={{ alignItems: 'flex-end' }}>
          <Field label="Select Games">
            {({ id }) => (
              <Select
                id={id}
                aria-label="Select Games"
                value={gameId}
                onChange={(e) => {
                  setGameId(e.target.value)
                  setSubGame('All')
                  markDirty()
                }}
                options={gameOptions}
              />
            )}
          </Field>
          <Field label="Select Sub Games">
            {({ id }) => (
              <Select
                id={id}
                aria-label="Select Sub Games"
                value={subGame}
                onChange={(e) => {
                  setSubGame(e.target.value)
                  markDirty()
                }}
                options={subOptions}
              />
            )}
          </Field>
          <Field label="Select Type">
            {({ id }) => (
              <Select
                id={id}
                aria-label="Select Type"
                value={typeName}
                onChange={(e) => {
                  setTypeName(e.target.value)
                  markDirty()
                }}
                options={[
                  { value: 'All', label: 'All' },
                  { value: 'Match Odds', label: 'Match Odds' },
                  { value: 'Bookmaker', label: 'Bookmaker' },
                  { value: 'Fancy', label: 'Fancy' },
                ]}
              />
            )}
          </Field>
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value)
                  markDirty()
                }}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value)
                  markDirty()
                }}
              />
            )}
          </Field>
          <Button
            variant="primary"
            busy={matrixQ.isFetching}
            onClick={() => {
              setApplied({
                memberScope,
                gameId,
                subGame,
                typeName,
                fromDate,
                toDate,
              })
              setSearched(true)
            }}
          >
            Search
          </Button>
        </div>
      </div>

      <Card
        flush
        title="Activity"
        subtitle={`${account.username} · ${applied.memberScope === 'agent' ? 'Agent' : 'All Members'} · ${customLabel}`}
      >
        {matrixQ.isFetching ? (
          <div
            className="w7-col"
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              gap: 12,
              minHeight: 220,
              padding: 'var(--w7-s5)',
            }}
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <Spinner size={22} />
            <p className="w7-dim" style={{ margin: 0 }}>
              Loading activity…
            </p>
          </div>
        ) : (
          <DataTable
            label="Activity matrix"
            rows={rows}
            columns={columns}
            rowKey={(row) => row.id}
            empty={
              <StateBlock icon="activity" title="No activity figures">
                Win, commission, P&amp;L and turnover for {account.username} would fill this sheet.
              </StateBlock>
            }
          />
        )}
      </Card>
      <p className="w7-dim" style={{ margin: 0, fontSize: 12 }}>
        Figures in {displayMoneyCompact(0, ccy).replace(/[\d.,]+/, '…')} for {account.username} only
        — not the signed-in seat.
      </p>
    </div>
  )
}

/* ============================================================== balance == */

export function BalanceTab({ account }: { account: Account }) {
  const ccy = parseCurrencyType(account.currencyType)
  const lines = balanceLinesFromAccount(account)
  const usage = limitUsage(account.exposure, account.creditLimit)

  type Row = (typeof lines)[number]
  const columns: Column<Row>[] = [
    {
      id: 'label',
      header: 'Item',
      cell: (row) => row.label,
      sortValue: (row) => row.label,
    },
    {
      id: 'value',
      header: 'Amount',
      numeric: true,
      cell: (row) => <Amount value={row.value} currency={account.currencyType} alreadyDisplay />,
      sortValue: (row) => row.value,
    },
  ]

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s5)' }}>
      <div className="w7-grid w7-grid-4">
        <Kpi
          label="Live balance"
          value={displayMoneyCompact(account.balance, ccy)}
          exact={displayMoney(account.balance, ccy)}
        />
        <Kpi
          label="Credit limit"
          value={displayMoneyCompact(account.creditLimit, ccy)}
          exact={displayMoney(account.creditLimit, ccy)}
        />
        <Kpi
          label="Open exposure"
          value={displayMoneyCompact(account.exposure, ccy)}
          exact={displayMoney(account.exposure, ccy)}
          tone={usage > 1 ? 'loss' : usage > 0.8 ? 'warn' : 'neutral'}
        />
        <Kpi
          label="Settlement"
          value={<Money value={round2(account.settlement)} signed currency={ccy} alreadyDisplay />}
          exact={displayMoney(account.settlement, ccy, { signed: true })}
          tone={account.settlement > 0 ? 'profit' : account.settlement < 0 ? 'loss' : 'neutral'}
        />
      </div>
      <Card flush title="Balance" subtitle={`${account.username} · Lab Detail balance sheet`}>
        <DataTable
          label="Balance sheet"
          rows={lines}
          columns={columns}
          rowKey={(row) => row.id}
        />
      </Card>
    </div>
  )
}

/* ============================================================== bet list == */

type BetListDraft = {
  fromDate: string
  toDate: string
  /** Lab ui-select `formData.name.username` — null until a pick. */
  memberUsername: string | null
  /** Lab Highlight Ip — client-only row/cell highlight. */
  highlightIp: string
  gameId: string
  subGame: string
  eventName: string
  marketName: string
  stakeFrom: string
  stakeTo: string
  time: MemberBetTime
  betType: MemberBetType
}

function initialBetListDraft(today: string): BetListDraft {
  return {
    fromDate: today,
    toDate: today,
    memberUsername: null,
    highlightIp: '',
    gameId: 'All',
    subGame: 'All',
    eventName: 'All',
    marketName: 'All',
    stakeFrom: '',
    stakeTo: '',
    time: 'Current',
    betType: 'Matched',
  }
}

/** Active filters tucked under More (dates + member stay on the bar). */
function betListMoreFilterCount(d: BetListDraft): number {
  let n = 0
  if (d.highlightIp.trim()) n += 1
  if (d.gameId !== 'All' && d.gameId !== 'all') n += 1
  if (d.subGame !== 'All') n += 1
  if (d.eventName !== 'All' && d.eventName !== '') n += 1
  if (d.marketName !== 'All' && d.marketName !== '') n += 1
  if (d.stakeFrom.trim()) n += 1
  if (d.stakeTo.trim()) n += 1
  return n
}

export function BetListTab({ account }: { account: Account; range: Range }) {
  const today = isoDayLocal()
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE
  const [draft, setDraft] = useState<BetListDraft>(() => initialBetListDraft(today))
  const [applied, setApplied] = useState(draft)
  const [searched, setSearched] = useState(true)
  const [moreOpen, setMoreOpen] = useState(false)
  const moreAnchor = useRef<HTMLButtonElement>(null)

  const games = useGames()
  const draftGameId =
    draft.gameId === 'All' || draft.gameId === 'all' ? null : draft.gameId
  const appliedGameId =
    applied.gameId === 'All' || applied.gameId === 'all' ? null : applied.gameId
  const subGames = useSubGames(draftGameId)
  const eventsQuery = {
    range: { from: draft.fromDate, to: draft.toDate },
    ...(draftGameId ? { game: draftGameId } : {}),
    ...(draft.subGame && draft.subGame !== 'All' ? { subGame: draft.subGame } : {}),
    name: { _id: account.id },
  }
  const { data: pllEvents = [], isLoading: eventsLoading } = useEventsForPll(eventsQuery)

  const stakeFromNum = applied.stakeFrom.trim() === '' ? undefined : Number(applied.stakeFrom)
  const stakeToNum = applied.stakeTo.trim() === '' ? undefined : Number(applied.stakeTo)

  const { data, isLoading } = useMemberBets({
    accountId: account.id,
    accountRole: account.role,
    source: 'sportsbook',
    fromDate: applied.fromDate,
    toDate: applied.toDate,
    time: applied.time,
    betType: applied.betType,
    page,
    pageSize,
    game: appliedGameId ?? undefined,
    subGame: applied.subGame,
    eventName: applied.eventName,
    marketName: applied.marketName,
    stakeFrom:
      stakeFromNum != null && Number.isFinite(stakeFromNum) ? stakeFromNum : undefined,
    stakeTo: stakeToNum != null && Number.isFinite(stakeToNum) ? stakeToNum : undefined,
    memberUsername: applied.memberUsername,
    enabled: searched,
  })

  const rows = data?.rows ?? []
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize))
  const highlightIp = applied.highlightIp.trim()

  /** Apply filter changes immediately (dates / member / More) — no Search gate. */
  const applyFilters = (partial: Partial<BetListDraft>) => {
    setDraft((d) => ({ ...d, ...partial }))
    setApplied((a) => ({ ...a, ...partial }))
    setPage(1)
    setSearched(true)
  }

  const clearAllExtraFilters = () => {
    applyFilters({
      memberUsername: null,
      highlightIp: '',
      gameId: 'All',
      subGame: 'All',
      eventName: 'All',
      marketName: 'All',
      stakeFrom: '',
      stakeTo: '',
    })
  }

  // Lab member-betlist games = All + SportsBook only.
  const sportsBookGames = (games.data ?? []).filter((g) => g.name === 'SportsBook')
  const gameOptions = [
    { value: 'All', label: 'All' },
    ...sportsBookGames.map((g) => ({ value: g._id, label: g.name })),
  ]
  const subOptions = [
    { value: 'All', label: 'All' },
    ...(subGames.data ?? []).map((name) => ({ value: name, label: name })),
  ]
  const eventOptions = [
    { value: 'All', label: 'All' },
    ...pllEvents.map((name) => ({ value: name, label: name })),
  ]
  // Lab cascade: Event after Game + Sub Game are chosen (not All).
  const showEventPicker =
    draft.gameId !== 'All' &&
    draft.gameId !== 'all' &&
    draft.subGame !== 'All'
  // Market only after a concrete Event (Lab `ng-if="markets.length>0"` after getMarkets).
  const showMarketPicker = draft.eventName !== 'All' && draft.eventName !== ''
  const moreCount = betListMoreFilterCount(draft)

  const activeFilters = useMemo((): ActiveFilter[] => {
    const chips: ActiveFilter[] = []
    if (draft.memberUsername) {
      chips.push({
        id: 'member',
        label: `Member: ${draft.memberUsername}`,
        removeLabel: 'Clear member name filter',
        onRemove: () => applyFilters({ memberUsername: null }),
      })
    }
    if (draft.gameId !== 'All' && draft.gameId !== 'all') {
      const gameLabel =
        gameOptions.find((g) => g.value === draft.gameId)?.label ?? draft.gameId
      chips.push({
        id: 'game',
        label: `Game: ${gameLabel}`,
        removeLabel: 'Clear game filter',
        onRemove: () =>
          applyFilters({
            gameId: 'All',
            subGame: 'All',
            eventName: 'All',
            marketName: 'All',
          }),
      })
    }
    if (draft.subGame !== 'All') {
      chips.push({
        id: 'subGame',
        label: `Sub Game: ${draft.subGame}`,
        removeLabel: 'Clear sub game filter',
        onRemove: () =>
          applyFilters({ subGame: 'All', eventName: 'All', marketName: 'All' }),
      })
    }
    if (draft.eventName !== 'All' && draft.eventName !== '') {
      chips.push({
        id: 'event',
        label: `Event: ${draft.eventName}`,
        removeLabel: 'Clear event filter',
        onRemove: () => applyFilters({ eventName: 'All', marketName: 'All' }),
      })
    }
    if (draft.marketName !== 'All' && draft.marketName !== '') {
      chips.push({
        id: 'market',
        label: `Market: ${draft.marketName}`,
        removeLabel: 'Clear market filter',
        onRemove: () => applyFilters({ marketName: 'All' }),
      })
    }
    if (draft.stakeFrom.trim()) {
      chips.push({
        id: 'stakeFrom',
        label: `Stakes from: ${draft.stakeFrom}`,
        removeLabel: 'Clear stakes from filter',
        onRemove: () => applyFilters({ stakeFrom: '' }),
      })
    }
    if (draft.stakeTo.trim()) {
      chips.push({
        id: 'stakeTo',
        label: `Stakes to: ${draft.stakeTo}`,
        removeLabel: 'Clear stakes to filter',
        onRemove: () => applyFilters({ stakeTo: '' }),
      })
    }
    if (draft.highlightIp.trim()) {
      chips.push({
        id: 'highlightIp',
        label: `Highlight IP: ${draft.highlightIp.trim()}`,
        removeLabel: 'Clear highlight IP',
        onRemove: () => applyFilters({ highlightIp: '' }),
      })
    }
    return chips
    // applyFilters closes over setters — stable enough; depend on draft fields.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    draft.memberUsername,
    draft.gameId,
    draft.subGame,
    draft.eventName,
    draft.marketName,
    draft.stakeFrom,
    draft.stakeTo,
    draft.highlightIp,
    games.data,
  ])

  const columns: Column<MemberSportBookBet>[] = [
    {
      id: 'n',
      header: '#',
      width: 48,
      cell: (_b, i) => (page - 1) * pageSize + i + 1,
    },
    {
      id: 'member',
      header: 'Member',
      cell: (b) => b.userName || EMPTY,
      sortValue: (b) => b.userName,
    },
    {
      id: 'placed',
      header: 'Placed',
      width: 165,
      cell: (b) => (b.placedAt ? lll(b.placedAt) : EMPTY),
      sortValue: (b) => b.placedAt ?? '',
    },
    {
      id: 'matched',
      header: 'Matched Date',
      width: 165,
      cell: (b) => (b.matchedAt ? lll(b.matchedAt) : EMPTY),
      sortValue: (b) => b.matchedAt ?? '',
    },
    {
      id: 'event',
      header: 'Event',
      cell: (b) => b.eventName,
      sortValue: (b) => b.eventName,
    },
    {
      id: 'market',
      header: 'Market',
      cell: (b) => (
        <>
          {b.marketName === 'Multi Bet' ? 'Genie Combo Special' : b.marketName}
          {b.isBookmaker ? ' - Bookmaker' : ''}
        </>
      ),
      sortValue: (b) => b.marketName,
    },
    {
      id: 'selection',
      header: 'Selection',
      cell: (b) => (
        <span className="w7-row" style={{ gap: 6, alignItems: 'center' }}>
          <Chip tone={b.side === 'back' ? 'back' : 'lay'} size="sm">
            {b.side === 'back' ? 'Back' : 'Lay'}
          </Chip>
          {b.selection}
        </span>
      ),
      sortValue: (b) => b.selection,
    },
    {
      id: 'oddsPlaced',
      header: 'Odds Placed',
      numeric: true,
      cell: (b) => memberBetListOddsPlaced(b),
      sortValue: (b) => (b.isLineLadder ? (b.runs ?? 0) : b.oddsPlaced),
    },
    {
      id: 'oddsMatched',
      header: 'Odds Matched',
      numeric: true,
      cell: (b) => (b.oddsMatched ? b.oddsMatched.toFixed(3) : EMPTY),
      sortValue: (b) => b.oddsMatched,
    },
    {
      id: 'matchedAmt',
      header: 'Matched',
      numeric: true,
      cell: (b) => betListMatchedUnmatchedAmt(b.matched, b.currencyType, b.marketName),
      sortValue: (b) => b.matched,
    },
    {
      id: 'unmatchedAmt',
      header: 'Unmatched',
      numeric: true,
      cell: (b) => betListMatchedUnmatchedAmt(b.unmatched, b.currencyType, b.marketName),
      sortValue: (b) => b.unmatched,
    },
    {
      id: 'pl',
      header: 'Profit/Liability',
      numeric: true,
      cell: (b) => {
        const pl = memberBetProfitLiability(b, applied.time)
        const lost = applied.time === 'Past' && b.betWinStatus.toUpperCase() === 'LOST'
        const layOpen = applied.time !== 'Past' && b.side === 'lay'
        return (
          <span className={lost || layOpen ? 'w7-neg' : 'w7-pos'}>
            <Amount value={pl} currency={b.currencyType} alreadyDisplay />
          </span>
        )
      },
      sortValue: (b) => memberBetProfitLiability(b, applied.time),
    },
    ...(applied.time === 'Past'
      ? [
          {
            id: 'status',
            header: 'Status',
            cell: (b: MemberSportBookBet) => b.betWinStatus || b.status || EMPTY,
            sortValue: (b: MemberSportBookBet) => b.betWinStatus || b.status,
          } satisfies Column<MemberSportBookBet>,
        ]
      : []),
    {
      id: 'ip',
      header: 'IP',
      cell: (b) => (
        <span
          style={
            highlightIp && b.ip && b.ip === highlightIp
              ? { color: 'var(--w7-warn, #f0ad4e)' }
              : undefined
          }
        >
          {b.ip || EMPTY}
        </span>
      ),
      sortValue: (b) => b.ip,
    },
  ]

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <FilterBar
        label="SportBook bet list filters"
        active={activeFilters}
        onClearAll={activeFilters.length > 0 ? clearAllExtraFilters : undefined}
      >
        <div
          className="w7-col"
          style={{ flex: '1 1 100%', minWidth: 0, gap: 'var(--w7-s3)' }}
        >
        <div className="w7-filterbar-row" style={{ alignItems: 'flex-end' }}>
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={draft.fromDate}
                onChange={(e) => applyFilters({ fromDate: e.target.value })}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={draft.toDate}
                onChange={(e) => applyFilters({ toDate: e.target.value })}
              />
            )}
          </Field>
          <BetListMemberNamePicker
            rootId={account.id}
            value={draft.memberUsername}
            onChange={(username) => applyFilters({ memberUsername: username })}
          />
          <Button
            ref={moreAnchor}
            variant={moreCount > 0 || moreOpen ? 'secondary' : 'ghost'}
            icon="filter"
            aria-expanded={moreOpen}
            aria-haspopup="dialog"
            onClick={() => setMoreOpen((v) => !v)}
          >
            {moreCount > 0 ? `More (${moreCount})` : 'More'}
          </Button>
          <Popover
            open={moreOpen}
            onClose={() => setMoreOpen(false)}
            anchorRef={moreAnchor}
            label="More filters"
            align="end"
            width={340}
          >
            <div className="w7-betlist-more">
              <label className="w7-betlist-more-field">
                <span>Select Games</span>
                <Select
                  aria-label="Select Games"
                  value={draft.gameId}
                  onChange={(e) =>
                    applyFilters({
                      gameId: e.target.value,
                      subGame: 'All',
                      eventName: 'All',
                      marketName: 'All',
                    })
                  }
                  options={gameOptions}
                />
              </label>
              <label className="w7-betlist-more-field">
                <span>Select Sub Games</span>
                <Select
                  aria-label="Select Sub Games"
                  value={draft.subGame}
                  onChange={(e) =>
                    applyFilters({
                      subGame: e.target.value,
                      eventName: 'All',
                      marketName: 'All',
                    })
                  }
                  options={subOptions}
                />
              </label>
              {showEventPicker ? (
                <label className="w7-betlist-more-field">
                  <span>Select Event</span>
                  <Select
                    aria-label="Select Event"
                    value={draft.eventName}
                    disabled={eventsLoading}
                    onChange={(e) =>
                      applyFilters({ eventName: e.target.value || 'All', marketName: 'All' })
                    }
                    options={eventOptions}
                  />
                </label>
              ) : null}
              {showMarketPicker ? (
                <label className="w7-betlist-more-field">
                  <span>Select Market Type</span>
                  <Input
                    aria-label="Select Market Type"
                    placeholder="Select or search a market"
                    value={draft.marketName === 'All' ? '' : draft.marketName}
                    onChange={(e) =>
                      applyFilters({ marketName: e.target.value.trim() || 'All' })
                    }
                  />
                </label>
              ) : null}
              <div className="w7-betlist-more-row">
                <label className="w7-betlist-more-field">
                  <span>Stakes From</span>
                  <Input
                    type="number"
                    min={0}
                    aria-label="Stakes From"
                    placeholder="Stakes From Value"
                    value={draft.stakeFrom}
                    onChange={(e) => applyFilters({ stakeFrom: e.target.value })}
                  />
                </label>
                <label className="w7-betlist-more-field">
                  <span>Stakes To</span>
                  <Input
                    type="number"
                    min={0}
                    aria-label="Stakes To"
                    placeholder="Stakes To Value"
                    value={draft.stakeTo}
                    onChange={(e) => applyFilters({ stakeTo: e.target.value })}
                  />
                </label>
              </div>
              <label className="w7-betlist-more-field">
                <span>Highlight Ip</span>
                <Input
                  aria-label="Highlight Ip"
                  placeholder="search for ip"
                  value={draft.highlightIp}
                  onChange={(e) => {
                    setDraft((d) => ({ ...d, highlightIp: e.target.value }))
                    setApplied((a) => ({ ...a, highlightIp: e.target.value }))
                  }}
                />
              </label>
            </div>
          </Popover>
        </div>
        <div className="w7-col" style={{ gap: 'var(--w7-s2)', alignItems: 'flex-start' }}>
          <div className="w7-filterbar-row">
            <Segmented
              label="Bet list status"
              value={draft.time}
              onChange={(v) => {
                const next: BetListDraft = {
                  ...draft,
                  time: v,
                  betType: v === 'Past' ? 'Matched' : draft.betType,
                }
                setDraft(next)
                setApplied(next)
                setPage(1)
                setSearched(true)
              }}
              items={[
                { id: 'Current', label: 'Current' },
                { id: 'Past', label: 'Past' },
              ]}
            />
          </div>
          {draft.time === 'Current' ? (
            <div className="w7-filterbar-row">
              <Segmented
                label="Match state"
                value={draft.betType}
                onChange={(v) => {
                  const next = { ...draft, betType: v }
                  setDraft(next)
                  setApplied(next)
                  setPage(1)
                  setSearched(true)
                }}
                items={[
                  { id: 'Matched', label: 'Matched' },
                  { id: 'Unmatched', label: 'Unmatched' },
                ]}
              />
            </div>
          ) : null}
        </div>
        </div>
      </FilterBar>

      <Card
        flush
        title="SportBook Bet List"
        subtitle={`${account.username} · ${applied.fromDate} – ${applied.toDate} · ${applied.time}${
          applied.time === 'Current' ? ` · ${applied.betType}` : ''
        }`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={data?.total ?? 0}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label="SportBook bets"
          rows={rows}
          columns={columns}
          rowKey={(b) => b.id}
          loading={isLoading}
          skeletonRows={8}
          pinLastColumn={false}
          empty={
            <StateBlock icon="sport" title="No data found">
              No SportBook bets for {account.username} between {applied.fromDate} and{' '}
              {applied.toDate}.
            </StateBlock>
          }
        />
      </Card>
    </div>
  )
}

/* ============================================================ transfers == */

/**
 * Lab member-transfer-New parity in Strix UI: From/To + Search, Total Net Share
 * with Player Loss/Win legend, Date / Payer-Payee / Amount (Lab-signed colours).
 * Still reads analytics `GET /v1/agents/{id}/transfers` via useTransfers.
 */
export function TransfersTab({
  account,
  highlightRef,
}: {
  account: Account
  range: Range
  /** Ledger ref from palette / search (`?ref=`). */
  highlightRef?: string | null
}) {
  const toast = useToast()
  const today = isoDayLocal()
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({ fromDate: today, toDate: today })
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE

  const rangeLabel = `${applied.fromDate} – ${applied.toDate}`
  const { data, isLoading, isFetching } = useTransfers({
    accountId: account.id,
    range: { from: applied.fromDate, to: applied.toDate },
    page,
    pageSize,
    currencyType: account.currencyType,
    partyOnly: true,
  })

  const rows = (data?.rows ?? []) as TransferRow[]
  const totals = data?.totals
  const netShare = totals ? labTransferNetShare(totals.given, totals.taken) : 0
  const pageCount = Math.max(1, Math.ceil((data?.total ?? 0) / pageSize))
  const referenced = highlightRef
    ? rows.find((row) => row.ref === highlightRef)
    : undefined

  const columns: Column<TransferRow>[] = useMemo(
    () => [
      {
        id: 'at',
        header: 'Date',
        width: 165,
        cell: (t) => lll(t.at),
        sortValue: (t) => t.at,
      },
      {
        id: 'payerPayee',
        header: 'Payer/Payee',
        cell: (t) => labTransferPayerPayee(t.from?.username, t.to?.username),
        sortValue: (t) => labTransferPayerPayee(t.from?.username, t.to?.username),
      },
      {
        id: 'amount',
        header: 'Amount',
        numeric: true,
        cell: (t) => (
          <Money
            value={labTransferSignedAmount(t.amount, t.to?.username, account.username)}
            currency={account.currencyType}
            alreadyDisplay
          />
        ),
        sortValue: (t) => labTransferSignedAmount(t.amount, t.to?.username, account.username),
      },
    ],
    [account.username, account.currencyType],
  )

  const runSearch = () => {
    const days = labTransferRangeDays(fromDate, toDate)
    if (days > LAB_TRANSFER_MAX_DAYS) {
      toast.error(
        'Date range too wide',
        `The date you have selected exceeds ${LAB_TRANSFER_MAX_DAYS} days. Please try again.`,
      )
      return
    }
    setPage(1)
    setApplied({ fromDate, toDate })
  }

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      {highlightRef && !isLoading ? (
        referenced ? (
          <Banner tone="info" title={`Showing the transfer around ${highlightRef}`}>
            {lll(referenced.at)} ·{' '}
            {labTransferPayerPayee(referenced.from?.username, referenced.to?.username)} ·{' '}
            {money(referenced.amount, {
              signed: true,
              currency: account.currencyType,
              alreadyDisplay: true,
            })}
          </Banner>
        ) : (
          <Banner tone="warn" title={`${highlightRef} is not in this window`}>
            That reference is not among the transfers between {applied.fromDate} and{' '}
            {applied.toDate}. Widen the date range and Search again.
          </Banner>
        )
      ) : null}

      <div className="w7-filterbar" role="region" aria-label="Transfer statement filters">
        <div className="w7-transfer-filter-row">
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input id={id} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            )}
          </Field>
          <Button variant="primary" busy={isFetching} onClick={runSearch}>
            Search
          </Button>
          <div className="w7-transfer-net-share">
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 'var(--w7-s3)' }}>
              <span className="w7-muted" style={{ fontSize: 'var(--w7-fs-sm)' }}>
                Total Net Share
              </span>
              <Money value={netShare} currency={account.currencyType} alreadyDisplay />
            </div>
            <Legend
              items={[
                { label: 'Player Loss', color: 'var(--w7-loss)' },
                { label: 'Player Win', color: 'var(--w7-profit)' },
              ]}
            />
          </div>
        </div>
      </div>

      <Card
        flush
        title="Transfer Statements"
        subtitle={`${account.username} · ${rangeLabel}`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={data?.total ?? 0}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label="Transfer statements"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={isLoading}
          skeletonRows={8}
          empty={
            <StateBlock icon="transfers" title="No data found">
              No credit transfers for {account.username} between {rangeLabel}.
            </StateBlock>
          }
        />
      </Card>
    </div>
  )
}

/* ======================================================== login history == */

/**
 * Lab Login History: From/To + Search, Block / Remove Blocked Device Id,
 * table (# Name Date IP Device Browser OS ISP City) + Pager.
 * Data: Agent-API `POST /v1/userLog/getuserLog`.
 */
export function LoginHistoryTab({ account }: { account: Account }) {
  const toast = useToast()
  const yesterday = (() => {
    const d = new Date()
    d.setDate(d.getDate() - 1)
    return isoDayLocal(d)
  })()
  const today = isoDayLocal()
  const [fromDate, setFromDate] = useState(yesterday)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({ fromDate: yesterday, toDate: today })
  const [searchNonce, setSearchNonce] = useState(0)
  const [page, setPage] = useState(1)
  const pageSize = DEFAULT_PAGE_SIZE
  const [blockOpen, setBlockOpen] = useState(false)
  const [blockComment, setBlockComment] = useState('')
  const [removeOpen, setRemoveOpen] = useState(false)
  const [ipRow, setIpRow] = useState<LoginHistoryRow | null>(null)

  const { data, isLoading, isFetching, isError, error } = useLoginHistory({
    memberId: account.id,
    fromDate: applied.fromDate,
    toDate: applied.toDate,
    page,
    pageSize,
    searchNonce,
  })
  const saveBlock = useSaveMemberBlockedDeviceId()
  const deleteBlock = useDeleteMemberBlockedDeviceId()

  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / pageSize))
  const rangeLabel = `${applied.fromDate} – ${applied.toDate}`
  const loadError =
    isError && error instanceof Error ? error.message : isError ? 'Login history request failed' : null

  const columns: Column<LoginHistoryRow>[] = useMemo(
    () => [
      {
        id: 'n',
        header: '#',
        width: 48,
        cell: (_row, i) => (page - 1) * pageSize + i + 1,
      },
      {
        id: 'name',
        header: 'Name',
        cell: (row) => row.name || account.username,
        sortValue: (row) => row.name,
      },
      {
        id: 'date',
        header: 'Date',
        cell: (row) => lll(row.at),
        sortValue: (row) => row.at,
      },
      {
        id: 'ip',
        header: 'IP',
        cell: (row) =>
          row.ip ? (
            <button
              type="button"
              className="w7-linkish"
              onClick={() => setIpRow(row)}
              title="IP detail"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              <span aria-hidden="true" className="w7-muted">
                (i)
              </span>
              {row.ip}
            </button>
          ) : (
            EMPTY
          ),
        sortValue: (row) => row.ip,
      },
      {
        id: 'device',
        header: 'Device',
        cell: (row) => row.device || EMPTY,
        sortValue: (row) => row.device,
      },
      {
        id: 'browser',
        header: 'Browser',
        cell: (row) => row.browser || EMPTY,
        sortValue: (row) => row.browser,
      },
      {
        id: 'os',
        header: 'OS',
        cell: (row) => row.os || EMPTY,
        sortValue: (row) => row.os,
      },
      {
        id: 'isp',
        header: 'ISP',
        hideBelow: 1080,
        cell: (row) => row.isp || EMPTY,
        sortValue: (row) => row.isp,
      },
      {
        id: 'city',
        header: 'City',
        hideBelow: 900,
        cell: (row) => row.city || EMPTY,
        sortValue: (row) => row.city,
      },
    ],
    [account.username, page, pageSize],
  )

  const runSearch = () => {
    setPage(1)
    setApplied({ fromDate, toDate })
    // Same From/To as last apply → queryKey unchanged without nonce; always re-hit API.
    setSearchNonce((n) => n + 1)
  }

  const onBlock = async () => {
    try {
      const res = await saveBlock.mutateAsync({
        memberId: account.id,
        comment: blockComment,
      })
      toast.push({
        tone: 'success',
        message: `${account.username}${res.data?.msg ?? ' Device ID blocked successfully.'}`,
      })
      setBlockOpen(false)
      setBlockComment('')
    } catch (err) {
      toast.push({
        tone: 'error',
        message: 'Blocked',
        detail:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'Unable to block device id',
      })
    }
  }

  const onRemove = async () => {
    try {
      await deleteBlock.mutateAsync({ memberId: account.id })
      toast.push({
        tone: 'success',
        message: `${account.username} Blocked Device ID Removed successfully.`,
      })
      setRemoveOpen(false)
    } catch (err) {
      toast.push({
        tone: 'error',
        message: 'Blocked Device ID Removing error',
        detail:
          err instanceof ApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : 'There was an error while Removing Blocked Device ID',
      })
    }
  }

  const q = ipRow?.qualityScore ?? {}
  const qStr = (key: string) => {
    const v = q[key]
    if (v == null || v === '') return EMPTY
    return String(v)
  }

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Login history filters">
        <div className="w7-transfer-filter-row">
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input id={id} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            )}
          </Field>
          <Button type="button" variant="primary" busy={isFetching} onClick={runSearch}>
            Search
          </Button>
          <Button type="button" variant="primary" onClick={() => setBlockOpen(true)}>
            Block Device Id
          </Button>
          <Button type="button" variant="primary" onClick={() => setRemoveOpen(true)}>
            Remove Blocked Device Id
          </Button>
        </div>
      </div>

      <Card
        flush
        title="Login History"
        subtitle={`${account.username} · ${rangeLabel}`}
        footer={
          <Pager
            page={page}
            pageCount={pageCount}
            onChange={setPage}
            total={total}
            pageSize={pageSize}
          />
        }
      >
        <DataTable
          label="Login history"
          rows={rows}
          columns={columns}
          rowKey={(row) => row.id}
          loading={isLoading || isFetching}
          skeletonRows={6}
          empty={
            loadError ? (
              <StateBlock icon="warning" title="Login history unavailable">
                {loadError}
              </StateBlock>
            ) : (
              <StateBlock icon="activity" title="No data found">
                No login history for {account.username} between {rangeLabel}.
              </StateBlock>
            )
          }
        />
      </Card>

      <Modal
        open={blockOpen}
        onClose={() => {
          if (!saveBlock.isPending) {
            setBlockOpen(false)
            setBlockComment('')
          }
        }}
        title="Are you sure you want to block Device ID"
        closeDisabled={saveBlock.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button
              variant="secondary"
              onClick={() => {
                setBlockOpen(false)
                setBlockComment('')
              }}
              disabled={saveBlock.isPending}
            >
              No
            </Button>
            <Button variant="primary" busy={saveBlock.isPending} onClick={() => void onBlock()}>
              Yes
            </Button>
          </>
        }
      >
        <Field label="Comments">
          {({ id }) => (
            <Textarea
              id={id}
              rows={3}
              placeholder="Enter Comments"
              value={blockComment}
              onChange={(e) => setBlockComment(e.target.value)}
            />
          )}
        </Field>
      </Modal>

      <Modal
        open={removeOpen}
        onClose={() => {
          if (!deleteBlock.isPending) setRemoveOpen(false)
        }}
        title="Are you sure you want to Remove Blocked Device ID"
        closeDisabled={deleteBlock.isPending}
        footer={
          <>
            <span className="w7-spacer" />
            <Button
              variant="secondary"
              onClick={() => setRemoveOpen(false)}
              disabled={deleteBlock.isPending}
            >
              No
            </Button>
            <Button
              variant="danger"
              busy={deleteBlock.isPending}
              onClick={() => void onRemove()}
            >
              Yes
            </Button>
          </>
        }
      >
        <p className="w7-muted" style={{ margin: 0 }}>
          This removes blocked device ids linked to {account.username}.
        </p>
      </Modal>

      <Modal
        open={ipRow !== null}
        onClose={() => setIpRow(null)}
        title="Ip Detail"
        footer={
          <>
            <span className="w7-spacer" />
            <Button variant="secondary" onClick={() => setIpRow(null)}>
              Close
            </Button>
          </>
        }
      >
        {ipRow ? (
          <div className="w7-col" style={{ gap: 'var(--w7-s2)', fontSize: 'var(--w7-fs-sm)' }}>
            {(
              [
                ['ip', ipRow.ip],
                ['ISP', ipRow.isp || qStr('ISP')],
                ['city', ipRow.city || qStr('city')],
                ['country_code', qStr('country_code')],
                ['region', qStr('region')],
                ['organization', qStr('organization')],
                ['host', qStr('host')],
                ['fraud_score', qStr('fraud_score')],
                ['vpn', qStr('vpn')],
                ['proxy', qStr('proxy')],
                ['connection_type', qStr('connection_type')],
              ] as const
            ).map(([label, value]) => (
              <div
                key={label}
                style={{ display: 'grid', gridTemplateColumns: '140px 1fr', gap: 8 }}
              >
                <span className="w7-muted">{label}</span>
                <span>{value || EMPTY}</span>
              </div>
            ))}
          </div>
        ) : null}
      </Modal>
    </div>
  )
}

/* =========================================================== commission == */

/**
 * Lab `member-commission` parity in Strix UI: From/To + Search, then Fancy and
 * Bookmaker tiles showing upline commission (Lab debit-style when > 0).
 * Same Agent-API/demo source as Fancy & Bookmaker report; member tab stays
 * the two-tile sheet, not the report's upline/downline/profit grid.
 */
export function CommissionTab({ account }: { account: Account }) {
  const today = isoDayLocal()
  const [fromDate, setFromDate] = useState(today)
  const [toDate, setToDate] = useState(today)
  const [applied, setApplied] = useState({ fromDate: today, toDate: today })

  const rangeLabel = `${applied.fromDate} – ${applied.toDate}`
  const fancyQ = useFancyBookmakerCommission(
    {
      memberId: account.id,
      from: applied.fromDate,
      to: applied.toDate,
    },
    true,
  )
  const fancyUpline = fancyQ.data?.fancy.upline ?? 0
  const bookmakerUpline = fancyQ.data?.bookmaker.upline ?? 0

  return (
    <div className="w7-col" style={{ gap: 'var(--w7-s4)' }}>
      <div className="w7-filterbar" role="region" aria-label="Commission filters">
        <div className="w7-transfer-filter-row">
          <Field label="From Date">
            {({ id }) => (
              <Input
                id={id}
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
              />
            )}
          </Field>
          <Field label="To Date">
            {({ id }) => (
              <Input id={id} type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            )}
          </Field>
          <Button
            variant="primary"
            busy={fancyQ.isFetching}
            onClick={() => setApplied({ fromDate, toDate })}
          >
            Search
          </Button>
        </div>
      </div>

      {fancyQ.isLoading ? (
        <Skeleton height={160} />
      ) : (
        <div className="w7-grid w7-grid-2">
          <Kpi
            label="Fancy"
            value={<Amount value={labMemberCommissionDisplay(fancyUpline)} />}
            meta={`${account.username} · ${rangeLabel}`}
          />
          <Kpi
            label="Bookmaker"
            value={<Amount value={labMemberCommissionDisplay(bookmakerUpline)} />}
            meta={`${account.username} · ${rangeLabel}`}
          />
        </div>
      )}
    </div>
  )
}

/* ======================================================== net exposure == */

export { NetExposureTab } from '@/features/member/NetExposureTab'
